/**
 * M10 Audit log query REST client.
 */

import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

export type AuditLogEntry = {
  id: string;
  actor_user_id: string | null;
  actor_type: string;
  actor_ip: string | null;
  actor_user_agent: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  request_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
};

export type AuditLogPage = {
  items: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
};

export type AuditLogFacets = {
  actions: string[];
  entity_types: string[];
};

export type AuditLogFilters = {
  actor_user_id?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
};

function buildQuery(f: AuditLogFilters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

export const auditApi = {
  list: (filters: AuditLogFilters) => call<AuditLogPage>(`/audit-log${buildQuery(filters)}`),
  facets: () => call<AuditLogFacets>('/audit-log/_facets'),
  csvUrl: (filters: AuditLogFilters): string =>
    `${env.VITE_API_URL}/api/v1/audit-log.csv${buildQuery(filters)}`,
  async downloadCsv(filters: AuditLogFilters): Promise<Blob> {
    const token = useAuthStore.getState().accessToken;
    const res = await fetch(auditApi.csvUrl(filters), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      throw new ApiError(res.status, { detail: `HTTP ${res.status}` });
    }
    return res.blob();
  },
};
